/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */
import type { EChartsInstance } from 'echarts-for-react';
import { isEqual } from 'lodash';
import React from 'react';
import {
  getGridlineTimes,
  getGridlineInterval,
  getGridlineLabel,
} from './verticalGridlines';

// ECharts does not expose the axis scale and rendered grid bounds on its
// public instance type. Read them so the overlay follows the actual layout.
interface ChartAxisModel {
  axis: {
    scale: { getExtent: () => [number, number]; isBlank: () => boolean };
    grid: {
      getRect: () => { x: number; y: number; width: number; height: number };
    };
  };
}
interface ChartModelApi {
  getModel: () => {
    getComponent: (type: string, index: number) => ChartAxisModel | undefined;
  };
}

interface Gridline {
  x: number;
  top: number;
  bottom: number;
  label?: [string, string];
}

export const getVisibleTimeRange = (
  chart: EChartsInstance | null,
): [number, number] | undefined => {
  if (!chart || chart.isDisposed()) {
    return undefined;
  }
  const axis = (chart as unknown as ChartModelApi)
    .getModel()
    .getComponent('xAxis', 0)?.axis;
  return axis && !axis.scale.isBlank() ? axis.scale.getExtent() : undefined;
};

export const getVerticalGridlines = (
  chart: EChartsInstance,
  plotCount: number,
): Gridline[] => {
  if (chart.isDisposed()) {
    return [];
  }
  const model = (chart as unknown as ChartModelApi).getModel();
  const lines: Gridline[] = [];
  for (let index = 0; index < plotCount; index += 1) {
    const axis = model.getComponent('xAxis', index)?.axis;
    if (axis && !axis.scale.isBlank()) {
      const [start, end] = axis.scale.getExtent();
      const rect = axis.grid.getRect();
      const interval = getGridlineInterval(start, end);
      for (const timestamp of getGridlineTimes(start, end)) {
        const x = chart.convertToPixel({ xAxisIndex: index }, timestamp);
        if (Number.isFinite(x) && x >= rect.x && x <= rect.x + rect.width) {
          lines.push({
            x,
            top: rect.y,
            bottom: rect.y + rect.height,
            ...(index === plotCount - 1 && {
              label: getGridlineLabel(timestamp, interval),
            }),
          });
        }
      }
    }
  }
  return lines;
};

export const useVerticalGridlines = (
  plotCount: number,
): {
  lines: Gridline[];
  updateGridlines: (chart: EChartsInstance) => void;
} => {
  const [lines, setLines] = React.useState<Gridline[]>([]);
  const updateGridlines = React.useCallback(
    (chart: EChartsInstance): void => {
      const next = getVerticalGridlines(chart, plotCount);
      setLines((previous) => (isEqual(previous, next) ? previous : next));
    },
    [plotCount],
  );
  return { lines, updateGridlines };
};
